import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../../config/api';
import { RootState } from '../index';

export interface EmailAddress {
  id: number;
  email: string;
  label?: string;
  user_id: number;
  is_active: boolean;
  created_at: string;
  updated_at?: string;
}

export interface EmailAddressDropdown {
  id: number;
  email: string;
  label?: string;
}

export interface CreateEmailAddressRequest {
  email: string;
  label?: string;
}

export interface UpdateEmailAddressRequest {
  email?: string;
  label?: string;
  is_active?: boolean;
}

export interface AgentEmailAssignments {
  agent_id: number;
  to_emails: EmailAddressDropdown[];
  cc_emails: EmailAddressDropdown[];
  bcc_emails: EmailAddressDropdown[];
}

export interface UpdateAgentEmailAssignmentsRequest {
  to_emails: number[];
  cc_emails: number[];
  bcc_emails: number[];
}

export const emailAddressesApi = createApi({
  reducerPath: 'emailAddressesApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl('/api/v1/email-addresses'),
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as RootState).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['EmailAddress', 'AgentEmailAssignments'],
  endpoints: (builder) => ({
    // Email Address CRUD operations
    getEmailAddresses: builder.query<EmailAddressDropdown[], void>({
      query: () => '/',
      providesTags: ['EmailAddress'],
    }),
    
    createEmailAddress: builder.mutation<EmailAddress, CreateEmailAddressRequest>({
      query: (emailData) => ({
        url: '/',
        method: 'POST',
        body: emailData,
      }),
      invalidatesTags: ['EmailAddress'],
    }),
    
    updateEmailAddress: builder.mutation<EmailAddress, { id: number; data: UpdateEmailAddressRequest }>({
      query: ({ id, data }) => ({
        url: `/${id}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: ['EmailAddress', 'AgentEmailAssignments'],
    }),
    
    deleteEmailAddress: builder.mutation<{ message: string }, number>({
      query: (id) => ({
        url: `/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['EmailAddress', 'AgentEmailAssignments'],
    }),
    
    // Agent Email Assignments
    getAgentEmailAssignments: builder.query<AgentEmailAssignments, number>({
      query: (agentId) => `/agent/${agentId}/assignments`,
      providesTags: (result, error, agentId) => [
        { type: 'AgentEmailAssignments', id: agentId },
      ],
    }),
    
    updateAgentEmailAssignments: builder.mutation<
      AgentEmailAssignments, 
      { agentId: number; data: UpdateAgentEmailAssignmentsRequest }
    >({
      query: ({ agentId, data }) => ({
        url: `/agent/${agentId}/assignments`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (result, error, { agentId }) => [
        { type: 'AgentEmailAssignments', id: agentId },
        'EmailAddress',
      ],
    }),
  }),
});

export const {
  useGetEmailAddressesQuery,
  useCreateEmailAddressMutation,
  useUpdateEmailAddressMutation,
  useDeleteEmailAddressMutation,
  useGetAgentEmailAssignmentsQuery,
  useUpdateAgentEmailAssignmentsMutation,
} = emailAddressesApi;
